import { motion } from "framer-motion";
import { MessageCircle, Leaf, Hand, Heart } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const highlights = [
  { icon: Leaf, title: "Earthy & Organic", note: "Shaped from river clay — no plastic, no chemicals" },
  { icon: Hand, title: "Hand-Sculpted", note: "Every friend is moulded and painted by artisan hands" },
  { icon: Heart, title: "Safe for Little Hands", note: "Soft edges, gentle colours, endless smiles" },
];

export default function Exclusive() {
  return (
    <main data-testid="exclusive-page" className="pt-32 lg:pt-40">
      <div className="mx-auto max-w-7xl px-6 pb-16 text-center lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold"
        >
          Laxwedeepan Exclusive · The Playful Line
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="mx-auto mt-6 max-w-3xl font-display text-4xl font-medium leading-tight text-brand-ink md:text-6xl"
        >
          Laxwedeepan <em className="italic text-brand-maroon">Mud Toys</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: EASE }}
          className="mt-4 font-display text-2xl italic text-brand-peach md:text-3xl"
        >
          Earth, Art &amp; Childhood.
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.45, ease: EASE }}
        className="mx-auto max-w-6xl px-6 lg:px-12"
      >
        <div className="overflow-hidden rounded-sm border border-brand-border bg-[#F4EFE6]" data-testid="mud-toys-banner">
          <img
            src="/assets/mud-toys.jpg"
            alt="Cute handmade Laxwedeepan mud toys — clay elephant, bunnies and bird with rose petals"
            data-testid="mud-toys-image"
            className="aspect-[16/9] w-full object-cover md:aspect-[21/9]"
          />
          <div className="flex flex-col items-center gap-6 px-6 py-14 text-center md:py-16">
            <p className="max-w-xl text-base font-light leading-relaxed text-brand-ink/70 md:text-lg">
              Wide-eyed friends shaped from river clay — safe, earthy and smiling.
              For little hands, festival gifting and everyone who remembers being a child.
            </p>
            <a
              href="https://wa.me/919205511502?text=Namaste%20Laxwedeepan%2C%20I%20would%20like%20to%20order%20Mud%20Toys."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mud-toys-order-button"
              className="group inline-flex items-center gap-3 rounded-full bg-brand-maroon px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-ivory transition-colors duration-300 hover:bg-brand-gold"
            >
              <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
              Order Mud Toys
            </a>
          </div>
        </div>
      </motion.div>

      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-12">
        <div className="grid grid-cols-1 divide-y divide-brand-border border border-brand-border md:grid-cols-3 md:divide-x md:divide-y-0">
          {highlights.map((h, i) => (
            <motion.div
              key={h.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
              className="flex flex-col items-center gap-4 px-8 py-14 text-center transition-colors duration-500 hover:bg-brand-cream/50"
              data-testid={`mud-toys-highlight-${i + 1}`}
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-brand-gold/60">
                <h.icon className="h-6 w-6 text-brand-maroon" strokeWidth={1.25} />
              </span>
              <h3 className="font-display text-2xl font-medium text-brand-ink">{h.title}</h3>
              <p className="text-sm font-light text-brand-ink/60">{h.note}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
